///<reference types="chrome"/>
import {Injectable} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {getQuickEntryExtensionId} from '@app/util/extension-id.helper';
import {UserApiService} from '@app/services/api/user-api.service';

export type ExtensionStatus = 'not-installed' | 'installed-logged-out' | 'installed-logged-in';

export interface ExtensionPingResponse {
  installed: true;
  loggedIn: boolean;
  version: string;
}

const PING_TIMEOUT_MS = 900;

@Injectable({providedIn: 'root'})
export class ExtensionStatusService {

  constructor(private userApiService: UserApiService) {
  }

  /**
   * Pushes the current user's JWT into the extension via the same
   * externally_connectable channel checkStatus() pings.
   */
  async login(): Promise<boolean> {
    if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
      return false;
    }

    try {
      const response = await firstValueFrom(this.userApiService.getAccessToken());
      const jwt = response.data.accessToken;

      const loginResponse: any = await chrome.runtime.sendMessage(getQuickEntryExtensionId(), {jwt});
      return !(loginResponse && !loginResponse.success);
    } catch (e) {
      return false;
    }
  }

  checkStatus(): Promise<ExtensionStatus> {
    if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
      return Promise.resolve('not-installed');
    }

    let pingPromise: Promise<ExtensionStatus>;
    try {
      pingPromise = chrome.runtime.sendMessage(getQuickEntryExtensionId(), {type: 'PING'})
        .then((response: ExtensionPingResponse | undefined): ExtensionStatus => {
          if (!response?.installed) {
            return 'not-installed';
          }
          return response.loggedIn ? 'installed-logged-in' : 'installed-logged-out';
        })
        .catch((): ExtensionStatus => 'not-installed');
    } catch (e) {
      pingPromise = Promise.resolve('not-installed');
    }

    const timeoutPromise: Promise<ExtensionStatus> = new Promise((resolve) => {
      setTimeout(() => resolve('not-installed'), PING_TIMEOUT_MS);
    });

    return Promise.race([pingPromise, timeoutPromise]);
  }
}
